import React, { CSSProperties, useMemo } from 'react';
import {
    useCountClientsQuery, useCountContentQuery, useCountModelsQuery, useCountProjectsQuery, useCountUsersQuery,
    useGetClientsShortQuery, useGetProjectsShortQuery, useGetModelsShortQuery,
    useGetContentShortQuery, useGetUsersShortQuery
} from '../../generated/graphql';
import { Users, FolderKanban, Box, FileText, UserCheck, ArrowUpRight, TrendingUp } from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import RecentCreationsPanel from '../../components/main/RecentCreationsPanel/RecentCreationsPanel';
import { useNavigate } from 'react-router-dom';

type DashboardProps = {
    style?: CSSProperties | undefined;
};

/** Count nodes whose epoch‑time `createdTime` falls within the current month */
function countCreatedThisMonth(
    nodes: Array<{ createdTime?: string | null } | null | undefined> | null | undefined,
    startEpoch: number,
    endEpoch: number
): number {
    if (!nodes) return 0;
    return nodes.filter((n) => {
        const t = Number(n?.createdTime);
        return !isNaN(t) && t >= startEpoch && t < endEpoch;
    }).length;
}

const statConfig = [
    { key: 'clients', label: 'Clients', icon: Users, color: 'var(--accent)', bgColor: 'var(--accent-soft)', path: '/clients' },
    { key: 'projects', label: 'Projects', icon: FolderKanban, color: 'var(--success)', bgColor: 'var(--success-soft)', path: '/projects' },
    { key: 'models', label: 'Models', icon: Box, color: 'var(--info)', bgColor: 'var(--info-soft)', path: null },
    { key: 'contents', label: 'Contents', icon: FileText, color: 'var(--warning)', bgColor: 'var(--warning-soft)', path: null },
    { key: 'users', label: 'User Count', icon: UserCheck, color: '#8b5cf6', bgColor: '#f5f3ff', path: null },
];

const Dashboard = (props: DashboardProps) => {
    const { style } = props;
    const navigate = useNavigate();
    const clientCount = useCountClientsQuery();
    const projectCount = useCountProjectsQuery();
    const modelCount = useCountModelsQuery();
    const userCount = useCountUsersQuery();
    const contentCount = useCountContentQuery();

    // Short‑list queries (return createdTime as epoch string for every record)
    const clientsShort = useGetClientsShortQuery();
    const projectsShort = useGetProjectsShortQuery();
    const modelsShort = useGetModelsShortQuery();
    const contentsShort = useGetContentShortQuery();
    const usersShort = useGetUsersShortQuery();

    // Epoch boundaries for the current month (in seconds)
    const { startEpoch, endEpoch } = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return {
            startEpoch: Math.floor(start.getTime() / 1000),
            endEpoch: Math.floor(end.getTime() / 1000),
        };
    }, []);

    const counts: Record<string, number | undefined> = {
        clients: clientCount.data?.ClientCount,
        projects: projectCount.data?.ProjectCount,
        models: modelCount.data?.ModelCount,
        contents: contentCount.data?.ContentCount,
        users: userCount.data?.UserCount,
    };

    const thisMonthCounts: Record<string, number | undefined> = {
        clients: clientsShort.data ? countCreatedThisMonth(clientsShort.data.ClientList?.nodes, startEpoch, endEpoch) : undefined,
        projects: projectsShort.data ? countCreatedThisMonth(projectsShort.data.ProjectList?.nodes, startEpoch, endEpoch) : undefined,
        models: modelsShort.data ? countCreatedThisMonth(modelsShort.data.ModelList?.nodes, startEpoch, endEpoch) : undefined,
        contents: contentsShort.data ? countCreatedThisMonth(contentsShort.data.ContentList?.nodes, startEpoch, endEpoch) : undefined,
        users: usersShort.data ? countCreatedThisMonth(usersShort.data.UserList?.nodes, startEpoch, endEpoch) : undefined,
    };

    const handleRefresh = () => {
        clientCount.refetch();
        projectCount.refetch();
        modelCount.refetch();
        userCount.refetch();
        contentCount.refetch();
        clientsShort.refetch();
        projectsShort.refetch();
        modelsShort.refetch();
        contentsShort.refetch();
        usersShort.refetch();
    };

    return (
        <div className="page-container" style={{ ...style, overflowY: 'auto' }} data-testid="Dashboard">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Dashboard</h1>
                    <p className="page-subtitle">Overview of your content management system</p>
                </div>
                <RefreshButton onRefresh={handleRefresh} className="btn-secondary" iconSize={16} />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 animate-slide-up">
                {statConfig.map((stat) => {
                    const IconComponent = stat.icon;
                    const value = counts[stat.key];
                    const thisMonthValue = thisMonthCounts[stat.key];
                    return (
                        <div
                            key={stat.key}
                            className={`card p-5 ${stat.path ? 'cursor-pointer' : ''} group`}
                            onClick={stat.path ? () => navigate(stat.path!) : undefined}
                            style={stat.path ? { cursor: 'pointer' } : {}}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: stat.bgColor }}
                                >
                                    <IconComponent size={20} style={{ color: stat.color }} />
                                </div>
                                <div className="flex items-center gap-2">
                                    {thisMonthValue !== undefined && thisMonthValue > 0 && (
                                        <span
                                            className="hidden min-[480px]:flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                                            style={{
                                                backgroundColor: 'var(--success-soft)',
                                                color: 'var(--success)',
                                            }}
                                            title={`Created this month: ${thisMonthValue}`}
                                        >
                                            <TrendingUp size={12} />
                                            +{thisMonthValue}<span className="hidden xl:inline"> this month</span>
                                        </span>
                                    )}
                                    {stat.path && (
                                        <ArrowUpRight
                                            size={16}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{ color: 'var(--text-tertiary)' }}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="stat-value">
                                {value !== undefined ? value.toLocaleString() : '—'}
                            </div>
                            <div className="stat-label mt-1">{stat.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* Recent Creations */}
            <RecentCreationsPanel maxItems={20} />

            {/* Quick Actions */}
            <div>
                <h2 className="section-title mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        className="card-interactive p-5 text-left"
                        onClick={() => navigate('/clients')}
                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-card)', cursor: 'pointer', outline: 'none' }}
                    >
                        <Users size={20} className="mb-3" style={{ color: 'var(--accent)' }} />
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Browse Clients</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                            View and manage all client records
                        </div>
                    </button>
                    <button
                        className="card-interactive p-5 text-left"
                        onClick={() => navigate('/projects')}
                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-card)', cursor: 'pointer', outline: 'none' }}
                    >
                        <FolderKanban size={20} className="mb-3" style={{ color: 'var(--success)' }} />
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Browse Projects</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                            Access project content and models
                        </div>
                    </button>
                    <button
                        className="card-interactive p-5 text-left"
                        onClick={() => navigate('/classifications')}
                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-card)', cursor: 'pointer', outline: 'none' }}
                    >
                        <FileText size={20} className="mb-3" style={{ color: 'var(--warning)' }} />
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Browse Classifications</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                            Manage classification types and rules
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;